<template>
    <IonLabel class="hint">
        Nach Berücksichtigung deiner
        <DetailsPopover label="Personalaufwendungen" text-only inline side="bottom">
            <p class="globalJustifyHyphenate">Die unmittelbaren Personalaufwendungen setzen sich zusammen aus:
            </p>
            <ul style="text-align: left">
                <li>Bruttolohn</li>
                <li>Arbeitgeberanteile der Sozialversicherungsbeiträge</li>
                <li>gesetzliche Umlagen</li>
                <li>Aufwendungen für weitere Gehaltsextras wie Gutscheine oder Mobilitätszulagen</li>
            </ul>
        </DetailsPopover>
        ergibt sich aus deinem Umsatz jeden Monat
        <DetailsPopover label="dein individueller Beitrag" text-only inline side="bottom">
            <!--<p class="globalJustifyHyphenate">Durch deine geleisteten Produktivstunden ergibt sich ein monatlicher Umsatz. Davon werden dein Gehalt und etwaige Gehaltsextras finanziert. Der verbleibende Überschussbeitrag wird im Verein angesammelt &ndash; parallel zur monatlichen <i>Wachstumsprämie</i>.</p>
            -->
            <p class="globalJustifyHyphenate">Am Ende jedes Kalendermonats wird die Differenz aus individuellem Umsatz und den unmittelbaren Personalaufwendungen bestimmt. Dieser Betrag ist der individuelle Beitrag zum Jahresüberschuss. Gleichzeitig dient er als maßgeblicher Schlüssel für die Verteilung der Überschüsse.</p>
            <p class="globalJustifyHyphenate">Die kumulierten Beiträge werden gleichzeitig als Verteilungsschlüssel für den Jahresüberschuss verwendet.</p>
        </DetailsPopover>
        zum Jahresüberschuss.
    </IonLabel>
    <Skeleton v-if="initialDataLoader.pending.value" :items="[`${APEX_SKELETON_DOUBLE_WITH_SUMMARY}`]"/>
    <template v-else>
        <div class="boxed margin-bottom-regular">
            <ApexChartValueLine :values="[
                {
                    label: 'Umsatz',
                    value: monthRanger.selectedRenderSet.value.revenue,
                    formatter: value => money.formatLargeMoney(value as Money)
                },
                {
                    label: 'Personalaufwand',
                    value: isSet(monthRanger.selectedRenderSet.value.pk1) ? money.abs(monthRanger.selectedRenderSet.value.pk1) : null,
                    formatter: value => money.formatLargeMoney(value as Money)
                },
                {
                    label: 'Mein Beitrag',
                    // value: `${formatLargeMoney(monthRanger.selectedRenderSet.value.excessContribution as Money, {signDisplay: 'exceptZero'})} | ${formatLargeMoney(monthRanger.selectedRenderSet.value.excessContributionCumulative as Money, {signDisplay: 'negative'})} kum.`
                    value: monthRanger.selectedRenderSet.value.excessContribution,
                    formatter: value => money.formatLargeMoney(value as Money, {signDisplay: 'exceptZero'})
                },
                // {
                //     label: 'kumuliert',
                //     value: monthRanger.selectedRenderSet.value.excessContributionCumulative,
                //     formatter: value => money.formatLargeMoney(value as Money, {signDisplay: 'never'})
                // }
            ]" />
            <ApexChartWrapper :options="apexOptionsRevenue"></ApexChartWrapper>
            <ApexChartWrapper :options="apexOptionsExcessContribution"></ApexChartWrapper>
        </div>
    </template>
    <IonLabel class="hint">
        Die Summe der individuellen Beiträge aller Mitarbeiter abzüglich der Gemeinkosten stellen den Jahresüberschuss dar. Je höher dein individueller Beitrag ist, <DetailsPopover label="desto höher" text-only inline side="bottom">
        <p class="globalJustifyHyphenate">Wie entsteht aus dem Beitrag zum Jahresüberschuss der Verteilungsschlüssel der Überschussanteile? Beispiel: es arbeiten zwei Fachkräfte bei <Ines/>. Der eine hat über das Jahr insgesamt 5.000 Euro zum Überschuss beigetragen, die andere 10.000. Unabhängig davon, wie viel Überschuss sich nun wirklich nach Abzug der Gemeinkosten etc. ergibt, sicher ist, dass der eine 1/3 Anteil am Überschuss bekommt, der andere 2/3 (also genau das Verhältnis von 5.000 zu 10.000).</p>
    </DetailsPopover> ist auch dein Anteil am Jahresüberschuss, den du nach der Jahresabrechnung bekommst.
    </IonLabel>
</template>

<script setup lang="ts">
import {computed, PropType, ref} from 'vue';
import {CalendarMonth} from "@/helper/calendar/calendarMonth";
import {DataLoader} from "@/helper/dataLoader";

import Skeleton from "@/components/Skeleton.vue";
import {Money, PayrollStatus} from "@/graphql/generated/graphql";

import {kapitelDateString, kapitelMonthString} from '@/graphql/kapitelTypes';
import {useEmployeeStore} from "@/store/employee";
import {fetchPayrollStatusList} from "@/helper/monthKPI/monthKpiPayroll";
import {toMap} from "@/helper/array";
import {MonthKPIApexMonthRangerFactory} from "@/helper/apexMonthRanger";
import * as money from "@/helper/money";
import {APEX_SKELETON_DOUBLE_WITH_SUMMARY, generateApexOptions} from "@/helper/apexchart";
import month from "@/helper/datetime/month";
import ApexChartValueLine from "@/views/components/ApexChartValueLine.vue";
import ApexChartWrapper from "@/views/components/ApexChartWrapper.vue";
import DetailsPopover from "@/views/components/DetailsPopover.vue";
import {IonLabel} from '@ionic/vue';
import {ifIsSet, isSet, nullish} from "@/helper/isset";
import Ines from "@/views/components/Ines.vue";

// props
const props = defineProps({
    selectedMonth: {
        type: String as PropType<kapitelMonthString>,
        required: true,
    }
});

// loader
const initialDataLoader = new DataLoader();

/**
 * month range & selection
 */
const monthRanger = MonthKPIApexMonthRangerFactory({
    selected: props.selectedMonth,
    renderer: (calendarMonth: CalendarMonth) => {

        const payrollStatusYTD = payrollStatusList.value
            .filter((ps) => month.isBetween(ps.month as kapitelMonthString, calendarMonth.getYear(), calendarMonth.getId()))

        const payrollStatus = payrollStatusMap.value.get(calendarMonth.getId())

        return {
            calendarMonth,
            revenue: payrollStatus?.revenue ?? null,
            pk1: payrollStatus?.pk1 ? money.sub(money.Zero, payrollStatus?.pk1) : null,
            excessContribution: payrollStatus?.dB1 ?? null,
            excessContributionCumulative: payrollStatusYTD.find((ps) => ps.month === calendarMonth.getId()) ? payrollStatusYTD.reduce((agg, ps) => money.add(agg, ps.dB1), money.Zero) : null,
            // recruitmentCashback: payrollStatus?.recruitmentCashback ?? null
        }
    }
})

const apexOptionsRevenue = computed(() => {
    const monthLabels = monthRanger.getMonthLabels()

    return generateApexOptions(
        monthRanger,
        {
            series: [
                {
                    name: "Umsatz",
                    data: monthRanger.allMonthsRenderSets.map(rs => ifIsSet(rs.value.revenue, money.toFloat, null))
                },
                {
                    name: "Personalaufwand",
                    data: monthRanger.allMonthsRenderSets.map(rs => ifIsSet(rs.value.pk1, money.toFloat, null)),
                }
            ],
            chart: {
                type: 'bar',
                stacked: true
            },
            yaxis: {
                labels: {
                    formatter: (value: number) => money.formatLargeFloat(value, {notation:"compact", compactDisplay: "short"}, 'en-US'),

                    // ensure equal width across all monthKPI charts
                    maxWidth: 50,
                    minWidth: 50
                }
            }
        },
        (idx: number) => {
            const rs = monthRanger.getRenderSet(idx)?.value
            return rs && isSet(rs.excessContribution) ? money.formatLargeMoney(rs.excessContribution, {signDisplay: "exceptZero"}) : null
        }
    )

})
const apexOptionsExcessContribution = computed(() => {
    const monthLabels = monthRanger.getMonthLabels()

    return generateApexOptions(
        monthRanger,
        {
            series: [
                {
                    name: "Überschussbeitrag",
                    data: monthRanger.allMonthsRenderSets.map(rs => ifIsSet(rs.value.excessContributionCumulative, money.toFloat, null)), // money.format
                },
                // {
                //     type: 'bar',
                //     name: "Überschussbeitrag",
                //     data: monthRanger.allMonthsRenderSets.map(rs => money.toFloat(rs.value.excessContribution)), // money.format
                // },
                // {
                //     name: "Wachstumsprämie",
                //     data: monthRanger.allMonthsRenderSets.map(rs => money.toFloat(rs.value.recruitmentCashback)), // money.format
                // }
            ],
            stroke: {curve: "stepline", },
            chart: { type: 'area'},
            yaxis: {
                labels: {
                    formatter: (value: number|nullish) => isSet(value) ? money.formatLargeFloat(value, {notation:"compact", compactDisplay: "short"}, 'en-US') : '-',

                    // ensure equal width across all monthKPI charts
                    maxWidth: 50,
                    minWidth: 50
                }
            }
        },
        (idx: number) => {
            const rs = monthRanger.getRenderSet(idx)?.value
            return rs && isSet(rs.excessContributionCumulative) ? (money.formatLargeMoney(rs.excessContributionCumulative, {signDisplay: "negative"}) + ' kum.') : null
        }
    )
})


/**
 * raw data
 */
const payrollStatusList = ref(<PayrollStatus[]>[])
const payrollStatusMap = computed(() => toMap(payrollStatusList.value, (a) => a.month as kapitelDateString))
initialDataLoader.add(
    async () => payrollStatusList.value = await fetchPayrollStatusList(monthRanger.allMonths)
)


/**
 * employee
 */
const employee = ref()
initialDataLoader.add(
    async () =>  employee.value = await useEmployeeStore().getEmployee()
)


/**
 * reset
 */

const reset = async () => initialDataLoader.reload()
defineExpose({reset})
reset()

</script>
